import axios from 'utils/axios';
import type {
    DirProposalFullType,
    ProposalDirectoryType,
    GetDirProposalListResultType,
    LeadCloudFileType,
    LeadProposalGenerationCreateDataType,
    LeadProposalGenerationType,
    LeadProposalFullType,
    ProposalSaveDataType,
} from '@econtractor/types';

export interface ProposalListParams {
    page?: number;
    search?: string;
    order?: string;
    onPage?: number;
    proposalType?: ProposalDirectoryType;
}

export const getDirList = async (params: ProposalListParams): Promise<GetDirProposalListResultType | null> => {
    try {
        const response = await axios('/proposal', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getDirProposal = async (proposalId: number | string): Promise<DirProposalFullType | null> => {
    try {
        const response = await axios(`/proposal/${proposalId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createDirProposal = async (payload: ProposalSaveDataType): Promise<number | false> => {
    try {
        const response = await axios.post('/proposal/new', payload);
        return response?.data?.proposalId || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveDirProposal = async (proposalId: number | string, payload: ProposalSaveDataType): Promise<boolean> => {
    try {
        const response = await axios.post(`/proposal/${proposalId}`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteDirProposal = async (proposalId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/proposal/${proposalId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getLeadProposal = async (leadId: number | string): Promise<LeadProposalFullType | null> => {
    try {
        const response = await axios(`/proposal/lead/${leadId}`);
        return response?.data?.proposal || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createEmptyLeadProposal = async (
    leadId: number | string,
): Promise<{ leadProposalId?: number; alreadyExists?: boolean } | false> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/new`);
        return response?.data || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const createLeadProposalFromDir = async (
    leadId: number | string,
    dirProposalId: number | string,
): Promise<{ leadProposalId?: number; alreadyExists?: boolean } | false> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/from-dir`, { dirProposalId });
        return response?.data || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveLeadProposal = async (
    leadId: number | string,
    leadProposalId: number | string,
    payload: ProposalSaveDataType,
): Promise<boolean> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/${leadProposalId}`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteLeadProposal = async (leadId: number | string, leadProposalId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/proposal/lead/${leadId}/${leadProposalId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

interface SendLeadProposalEmailPayload {
    email: string;
    file: LeadCloudFileType;
    proposalName?: string;
    mailContent: { subject: string; html: string };
}

export const sendLeadProposalEmail = async (
    leadId: number | string,
    payload: SendLeadProposalEmailPayload,
): Promise<boolean> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/send-email`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getLeadProposalGenerations = async (
    leadId: number | string,
): Promise<LeadProposalGenerationType[] | null> => {
    try {
        const response = await axios(`/proposal/lead/${leadId}/generations`);
        return response?.data?.rows || [];
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createLeadProposalGeneration = async (
    leadId: number | string,
    payload: LeadProposalGenerationCreateDataType,
): Promise<{ stat: boolean; generationId: number } | null> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/generations`, payload);
        return {
            stat: Boolean(response?.data?.stat),
            generationId: Number(response?.data?.generationId || 0),
        };
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const setLeadProposalGenerationAccept = async (
    leadId: number | string,
    generationId: number | string,
    proposavAccept: boolean,
    options?: { deactivateOthers?: boolean },
): Promise<boolean> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/generations/${generationId}/accept`, {
            proposavAccept,
            ...(options?.deactivateOthers ? { deactivateOthers: true } : {}),
        });
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const sendLeadProposalGenerationEmail = async (
    leadId: number | string,
    generationId: number | string,
    payload: {
        email: string;
        emailBody?: string;
        mailContent: { subject: string; html: string };
    },
): Promise<boolean> => {
    try {
        const response = await axios.post(`/proposal/lead/${leadId}/generations/${generationId}/send-email`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};
